import dayjs from "dayjs"
import { findPrinters, readFriendlyName } from "./browserPrint"

// Print speed in inches per second (^PR), for print, slew and backfeed
const PRINT_SPEED = 2

const FRIENDLY_NAMES_STORAGE_KEY = "kfc.zebra.friendlyNames"
const SELECTED_PRINTER_STORAGE_KEY = "kfc.zebra.selectedPrinterUid"

// localStorage can be unavailable (private mode, blocked storage): the printing keeps working without it
function readStorage(key, fallback) {
  try {
    const value = localStorage.getItem(key)
    return value === null ? fallback : JSON.parse(value)
  } catch {
    return fallback
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage unavailable: the value is only lost for the next page load
  }
}

/**
 * Printer chosen by the operator, kept across page reloads and steps.
 * Identified by its uid only: if it is missing from a search, it is reported as
 * not found, never replaced by another printer.
 */
export function getLastSelectedPrinterUid() {
  const uid = readStorage(SELECTED_PRINTER_STORAGE_KEY, "")
  return typeof uid === "string" ? uid : ""
}

export function setLastSelectedPrinterUid(uid) {
  writeStorage(SELECTED_PRINTER_STORAGE_KEY, typeof uid === "string" ? uid : "")
}

/** uid -> friendly name, shown without any request; read again only on a manual refresh */
function getStoredFriendlyNames() {
  const names = readStorage(FRIENDLY_NAMES_STORAGE_KEY, {})
  return names && typeof names === "object" ? names : {}
}

export function getStoredFriendlyName(uid) {
  const name = getStoredFriendlyNames()[uid]
  return typeof name === "string" ? name : ""
}

function storeFriendlyName(uid, name) {
  writeStorage(FRIENDLY_NAMES_STORAGE_KEY, { ...getStoredFriendlyNames(), [uid]: name })
}

/**
 * Label shown for a printer: its friendly name, else the name given by Browser Print
 * when it looks like a real name (for network printers it can be a comma-separated
 * status blob), else its uid, which contains the IP address for network printers.
 */
export function getPrinterLabel(printer) {
  if (!printer) return ""
  if (typeof printer.friendlyName === "string" && printer.friendlyName.length > 0) return printer.friendlyName
  const name = printer.name
  if (typeof name === "string" && name.length > 0 && name.length <= 64 && !name.includes(",")) return name
  return typeof printer.uid === "string" ? printer.uid : ""
}

/**
 * Last successful search, kept 5 minutes to avoid a scan on every modal opening.
 * An empty or failed search is never kept.
 */
const printerListCache = { list: [], timestamp: 0 }
const PRINTER_LIST_TTL_MS = 5 * 60 * 1000

function isPrinterListFresh() {
  return printerListCache.timestamp > 0 && (Date.now() - printerListCache.timestamp) < PRINTER_LIST_TTL_MS
}

/**
 * Search the printers and return them at once, with the names already known on this tablet.
 * Throws a BrowserPrintError when Browser Print is missing or silent, or finds no printer.
 *
 * @param {boolean} [forceRefresh=false] - bypass the cache (manual "Actualiser" only)
 */
export async function searchPrinters(forceRefresh = false) {
  if (forceRefresh || !isPrinterListFresh()) {
    printerListCache.timestamp = 0
    printerListCache.list = await findPrinters()
    printerListCache.timestamp = Date.now()
  }

  const names = getStoredFriendlyNames()
  return printerListCache.list.map(printer => ({ ...printer, friendlyName: names[printer.uid] || "" }))
}

const pendingNameReads = new Set()

/**
 * Read the printer names in background, all printers in parallel: the unknown ones only,
 * or all of them on a manual refresh (rereadKnown), so that a renamed printer is updated.
 * Calls onName(uid, name) as each new name arrives; a printer that does not answer keeps
 * its stored name, or its uid.
 */
export function fetchFriendlyNames(printers, onName, rereadKnown = false) {
  printers
    .filter(printer => printer?.uid && (rereadKnown || !printer.friendlyName) && !pendingNameReads.has(printer.uid))
    .forEach(async printer => {
      pendingNameReads.add(printer.uid)
      try {
        const name = await readFriendlyName(printer)
        if (name && name !== printer.friendlyName) {
          storeFriendlyName(printer.uid, name)
          onName(printer.uid, name)
        }
      } catch (e) {
        console.warn("Printer friendly name not read", printer.uid, e)
      } finally {
        pendingNameReads.delete(printer.uid)
      }
    })
}

// ^FH lets these characters print as-is instead of being read as ZPL commands. "_" goes first,
// otherwise the escapes added for "^" and "~" would be escaped again
function escapeZplText(text) {
  return String(text).replace(/_/g, "_5F").replace(/\^/g, "_5E").replace(/~/g, "_7E")
}

/**
 * Builds a ZPL card label from a plain data object instead of a PSE.
 * @param {{ title: string, stepProductionDate: string|Date, netWeight: number, dlc: string|Date, packagingDateLabel?: string }} data
 * @param {number} copyIndex
 * @param {number} copyTotal
 */
export function buildCardLabelZplFromData(data, copyIndex, copyTotal) {
  const title = data.title || ""
  const madeOn = dayjs(data.stepProductionDate ? data.stepProductionDate : new Date()).format("DD/MM/YY")
  const dlc = dayjs(data.dlc).format("DD/MM/YYYY")
  let weightVal = ""
  if (typeof data.netWeight === "number") weightVal = String(Math.round(data.netWeight * 100) / 100) + " kg"
  const pagination = String(copyIndex) + "/" + String(copyTotal)
  const packagingDateLabel = data.packagingDateLabel || ""
  const dateShift = packagingDateLabel ? 47 : 0

  const {titleLines, yShift} = computeTitleYShift(title)
  const labelHeight = 346 + yShift + dateShift

  const rows = [
    "^XA",
    // --- Printer configuration for ZD421 ---
    "^CI28",                    // UTF-8 encoding
    "^MNN",                     // Media tracking: continuous media (no gaps/marks) - use ^MNY for die-cut labels with gaps
    "^MMC",                     // Print mode: Cutter (cut after each label)
    "^LH0,0",                   // Label home position (origin)
    "^PW440",                   // Print width in dots (440 dots ≈ 55mm at 203dpi)
    `^LL${labelHeight}`,        // Label length in dots (variable)
    `^PR${PRINT_SPEED},${PRINT_SPEED},${PRINT_SPEED}`, // Print speed: print, slew, backfeed (slower = more reliable)
    "^MD15",                    // Media darkness
    // --- Label content ---
    "^FX Title block (line spacing 18)",
    `^FO0,68^A0N,29,31^FB440,${titleLines},18,L,0^FH^FD` + escapeZplText(title) + "^FS",
    // Underline after title
    `^FO0,${102 + yShift}^GB440,1,1^FS`,
  ]

  if (packagingDateLabel) {
    rows.push(
      // Row: À barquetter
      `^FO0,${116 + yShift}^A0N,29,31^FB440,1,0,L,0^FD` + packagingDateLabel + "^FS",
      // Underline after packaging date
      `^FO0,${149 + yShift}^GB440,1,1^FS`,
    )
  }

  rows.push(
    // Row 1: Fait le
    `^FO0,${116 + yShift + dateShift}^A0N,29,31^FB440,1,0,L,0^FDFait le : ` + madeOn + "^FS",
    // Underline after row 1
    `^FO0,${149 + yShift + dateShift}^GB440,1,1^FS`,
    // Row 2: DLC interne
    `^FO0,${163 + yShift + dateShift}^A0N,29,31^FB440,1,0,L,0^FDDLC interne : ` + dlc + "^FS",
    // Underline after row 2
    `^FO0,${196 + yShift + dateShift}^GB440,1,1^FS`,
    // Row 3: Poids
    `^FO0,${210 + yShift + dateShift}^A0N,29,31^FB440,1,0,L,0^FDPoids : ` + weightVal + "^FS",
    // Underline after row 3
    `^FO0,${243 + yShift + dateShift}^GB440,1,1^FS`,
    // Final underline
    `^FO0,${290 + yShift + dateShift}^GB440,1,1^FS`,
    // Pagination
    "^FX Bottom-right pagination",
    `^FO292,${306 + yShift + dateShift}^A0N,23,23^FB150,1,0,R,0^FD` + pagination + "^FS",
    "^XZ"
  )

  return rows.join("\n")
}

/**
 * Estimate how many lines the title will wrap into,
 * then compute the vertical shift (yShift) needed
 * so the rest of the label is pushed down.
 */
function computeTitleYShift(labelTitle, maxWidthDots = 372, charWidthDots = 13, lineHeightDots = 31, lineSpacingDots = 18) {
  // An empty title keeps one blank line, so the rest of the label does not move
  if (!labelTitle) return { titleLines: 1, yShift: 0 }

  const words = String(labelTitle).split(/\s+/)

  const lines = words.reduce((acc, word) => {
    const current = acc[acc.length - 1] + (acc[acc.length - 1] ? 1 : 0) + word.length
    if (current * charWidthDots <= maxWidthDots) {
      acc[acc.length - 1] = current
    } else {
      acc.push(word.length) // new line
    }
    return acc
  }, [0]).length

  return {
    titleLines: lines,
    yShift: (lines - 1) * (lineHeightDots + lineSpacingDots)
  }
}
